/** Authenticated WebSocket client with auto-reconnect. Provides an
 *  imperative subscribe API so features (chat, meetings, notifications,
 *  cubicles) can drop-in listen without wiring their own sockets. */
import { useEffect, useRef } from "react";
import { getToken, API } from "@/lib/api";

const listeners = new Set();
// Channels the client has asked the server to subscribe it to *after*
// the standard hello-frame auto-subscribe (user + threads + org). Kept
// so reconnects can replay them.
const pendingChannels = new Set();

function urlFromApi() {
  const base = API.replace(/^http/i, (m) => (m.toLowerCase() === "http" ? "ws" : "wss"));
  return `${base}/ws`;
}

let ws = null;
let reconnectDelay = 1000;
let heartbeat = null;
let intentionalClose = false;

function _sendSubscribe(channels) {
  if (!channels || channels.length === 0) return;
  if (ws && ws.readyState === 1) {
    try { ws.send(JSON.stringify({ type: "subscribe", channels })); }
    catch { /* noop */ }
  }
}

function connect() {
  const token = getToken();
  if (!token) return;
  intentionalClose = false;
  try {
    ws = new WebSocket(`${urlFromApi()}?token=${encodeURIComponent(token)}`);
  } catch { return; }

  ws.addEventListener("open", () => {
    reconnectDelay = 1000;
    heartbeat = setInterval(() => {
      try { ws?.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })); }
      catch { /* noop */ }
    }, 25000);
    // Replay any subscriptions the app had registered before this
    // (re)connect finished.
    if (pendingChannels.size > 0) {
      _sendSubscribe(Array.from(pendingChannels));
    }
  });
  ws.addEventListener("message", (e) => {
    let evt;
    try { evt = JSON.parse(e.data); } catch { return; }
    listeners.forEach((l) => {
      try { l(evt); } catch (err) { console.warn("WS listener err", err); }
    });
  });
  const cleanup = () => {
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = null;
    if (!intentionalClose) {
      const d = Math.min(reconnectDelay, 15000);
      reconnectDelay *= 2;
      setTimeout(() => connect(), d);
    }
  };
  ws.addEventListener("close", cleanup);
  ws.addEventListener("error", () => { try { ws?.close(); } catch { /* noop */ } });
}

export function disconnectWs() {
  intentionalClose = true;
  try { ws?.close(); } catch { /* noop */ }
  ws = null;
  pendingChannels.clear();
}

/** Ask the server to subscribe this socket to the given channels.
 *  Idempotent; safe to call multiple times. Auto-replays on reconnect. */
export function subscribeWs(channels) {
  if (typeof channels === "string") channels = [channels];
  if (!Array.isArray(channels)) return;
  channels.forEach((c) => pendingChannels.add(c));
  _sendSubscribe(channels);
}

/** Cancel a prior subscribe. Prevents replay on reconnect and asks the
 *  server to drop this socket from the channel roster immediately. */
export function unsubscribeWs(channels) {
  if (typeof channels === "string") channels = [channels];
  if (!Array.isArray(channels)) return;
  channels.forEach((c) => pendingChannels.delete(c));
  if (ws && ws.readyState === 1) {
    try { ws.send(JSON.stringify({ type: "unsubscribe", channels })); }
    catch { /* noop */ }
  }
}

/** Subscribe to every incoming event. Returns an unsubscribe fn. */
export function useWebSocket(onEvent) {
  const stableRef = useRef(onEvent);
  stableRef.current = onEvent;

  useEffect(() => {
    if (!ws || ws.readyState > 1) connect();
    const wrapped = (evt) => stableRef.current?.(evt);
    listeners.add(wrapped);
    return () => { listeners.delete(wrapped); };
  }, []);
}
